import {
  CheckboxFilterType,
  CtaButton,
  FilterGroup,
  MiniButton,
  Modal,
  RangeFilterType,
} from '@greystar/designsystem';
import {
  formatCurrency,
  getWindowLocation,
  isBrowser,
  useAnalytics,
  useFormattedAddress,
} from '@greystar/lib';
import { Property } from '@greystar/lib/src/types/property-types';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyFilters,
  decodeFiltersFromUrl,
  encodeFiltersToUrl,
  formatLease,
  formatPrices,
  getDynamicRoomOptions,
  getUniqueSortedLease,
  getUniqueSortedPrices,
  parseNumber,
} from './FloorPlanFilters-helper';
import { FloorPlanFiltersProps, RoomType } from './FloorPlanFilters-types';

const FloorPlanFilters = ({
  groupedFloorPlans,
  setFilteredFloorPlans,
  clearFiltersTrigger,
  filterLabel,
}: FloorPlanFiltersProps) => {
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [minPriceSelected, setMinPriceSelected] = useState('');
  const [maxPriceSelected, setMaxPriceSelected] = useState('');
  const [bathroomSelected, setBathroomSelected] = useState<string[]>([]);
  const [bedroomSelected, setBedroomSelected] = useState<string[]>([]);
  const [moveInSelected, setMoveInSelected] = useState<string[]>([]);
  const [maxLeaseSelected, setMaxLeaseSelected] = useState('');
  const [minLeaseSelected, setMinLeaseSelected] = useState('');
  const [filteredCount, setFilteredCount] = useState(0);
  const [tempFilters, setTempFilters] = useState({
    minPrice: '',
    maxPrice: '',
    minLease: '',
    maxLease: '',
    bedroomSelected: [] as string[],
    bathroomSelected: [] as string[],
    moveInSelected: [] as string[],
  });
  const analytics = useAnalytics();

  const prevClearTriggerRef = useRef<number | undefined>(undefined);

  const sitecoreContext = useSitecoreContext();
  const property = sitecoreContext.sitecoreContext.property as Property;
  const property_location = useFormattedAddress(property?.location);
  const pushDataLayerEvent = (
    event: string,
    additionalData: Partial<{
      action_type: string;
      action_value?: string;
      state?: string;
      property_location?: string;
      property_id: string;
      property_name: string;
      module_name?: string;
      inpage_location?: string;
      click_text?: string;
      click_type?: string;
      destination_url: string;
      props?: object;
    }> = {}
  ) => {
    const eventData = {
      event: event,
      action_type: additionalData.action_type,
      action_value: additionalData.action_value ?? '',
      state: property.location?.state,
      property_location: property_location,
      property_id: property.id,
      property_name: property.name,
      module_name: 'floor plan',
      inpage_location: 'middle',
      click_text: additionalData.click_text ?? '',
      click_type: 'internal',
      destination_url: getWindowLocation(),
      ...additionalData.props,
    };
    analytics.trackEvent(event, eventData);
  };

  const tempFilteredResults = useMemo(() => {
    if (!isFilterVisible) return [];

    const min = parseNumber(tempFilters.minPrice);
    const max = parseNumber(tempFilters.maxPrice);
    const minLease = parseNumber(tempFilters.minLease);
    const maxLease = parseNumber(tempFilters.maxLease);

    return (
      applyFilters({
        groupedFloorPlans,
        property,
        min,
        max,
        minLease,
        maxLease,
        bedroomSelected: tempFilters.bedroomSelected,
        bathroomSelected: tempFilters.bathroomSelected,
        moveInSelected: tempFilters.moveInSelected,
      }) ?? []
    );
  }, [tempFilters, groupedFloorPlans, property, isFilterVisible]);

  useEffect(() => {
    if (!isFilterVisible) return;
    const count = tempFilteredResults.reduce((acc, group) => acc + group.units.length, 0);
    setFilteredCount(count);
  }, [tempFilteredResults, isFilterVisible]);

  useEffect(() => {
    if (clearFiltersTrigger && clearFiltersTrigger !== prevClearTriggerRef.current) {
      clearFilters(true);
      prevClearTriggerRef.current = clearFiltersTrigger;
    }
  }, [clearFiltersTrigger]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('filters') && groupedFloorPlans) {
      const { min, max, minLease, maxLease, bedroomSelected, bathroomSelected, moveInSelected } =
        decodeFiltersFromUrl(window.location.search);

      const minVal = parseNumber(min);
      const maxVal = parseNumber(max);
      const minLeaseVal = parseNumber(minLease);
      const maxLeaseVal = parseNumber(maxLease);

      const filtered = applyFilters({
        groupedFloorPlans,
        property,
        min: minVal,
        max: maxVal,
        minLease: minLeaseVal,
        maxLease: maxLeaseVal,
        bedroomSelected,
        bathroomSelected,
        moveInSelected,
      });

      setFilteredFloorPlans(filtered ?? []);
    } else if (groupedFloorPlans) {
      setFilteredFloorPlans(groupedFloorPlans);
    }
  }, [groupedFloorPlans]);

  useEffect(() => {
    const { min, max, minLease, maxLease, bedroomSelected, bathroomSelected, moveInSelected } =
      decodeFiltersFromUrl(window.location.search);

    setMinPriceSelected(min);
    setMaxPriceSelected(max);
    setMinLeaseSelected(minLease);
    setMaxLeaseSelected(maxLease);
    setBedroomSelected(bedroomSelected);
    setBathroomSelected(bathroomSelected);
    setMoveInSelected(moveInSelected);
  }, []);

  const allPrices = getUniqueSortedPrices(groupedFloorPlans);
  const allLeaseTerms = getUniqueSortedLease(groupedFloorPlans);

  const getAppliedFilterGroupCount = () => {
    let count = 0;
    if (minPriceSelected || maxPriceSelected) count++;
    if (bedroomSelected.length > 0) count++;
    if (bathroomSelected.length > 0) count++;
    if (moveInSelected.length > 0) count++;
    if (minLeaseSelected || maxLeaseSelected) count++;
    return count;
  };

  const updateMinPriceSelected = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setTempFilters((prev) => ({ ...prev, minPrice: value }));

    pushDataLayerEvent('filters_selection', {
      action_type: 'budget - min price',
      action_value: value,
      click_text: `$${value}`,
    });
  };

  const updateMaxPriceSelected = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setTempFilters((prev) => ({ ...prev, maxPrice: value }));

    pushDataLayerEvent('filters_selection', {
      action_type: 'budget - max price',
      action_value: value,
      click_text: `$${value}`,
    });
  };

  const updateMinLease = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setTempFilters((prev) => ({ ...prev, minLease: value }));

    pushDataLayerEvent('filters_selection', {
      action_type: 'lease term - min',
      action_value: value,
      click_text: `$${value}`,
    });
  };

  const updateMaxLease = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setTempFilters((prev) => ({ ...prev, maxLease: value }));

    pushDataLayerEvent('filters_selection', {
      action_type: 'lease term - max',
      action_value: value,
      click_text: `$${value}`,
    });
  };

  const openFilterModal = () => {
    setTempFilters({
      minPrice: minPriceSelected,
      maxPrice: maxPriceSelected,
      minLease: minLeaseSelected,
      maxLease: maxLeaseSelected,
      bedroomSelected: [...bedroomSelected],
      bathroomSelected: [...bathroomSelected],
      moveInSelected: [...moveInSelected],
    });
    setIsFilterVisible(true);

    if (isBrowser) {
      pushDataLayerEvent('filters_open', {
        action_type: 'all filters - open',
        click_text: filterLabel,
      });
    }

    if (isBrowser) document.body.style.overflow = 'hidden';
  };

  const closeFilterModal = () => {
    setIsFilterVisible(false);
    if (isBrowser) {
      pushDataLayerEvent('filters_close', {
        action_type: 'all filters - close',
        click_text: filterLabel,
      });
    }

    if (isBrowser) document.body.style.overflow = 'auto';
  };

  const clearFilters = (shouldApplyImmediately = false) => {
    const clearedValues = {
      minPrice: '',
      maxPrice: '',
      minLease: '',
      maxLease: '',
      bedroomSelected: [],
      bathroomSelected: [],
      moveInSelected: [],
    };

    setTempFilters(clearedValues);

    if (shouldApplyImmediately) {
      setMinPriceSelected('');
      setMaxPriceSelected('');
      setMinLeaseSelected('');
      setMaxLeaseSelected('');
      setBedroomSelected([]);
      setBathroomSelected([]);
      setMoveInSelected([]);

      setFilteredFloorPlans(groupedFloorPlans);

      if (isBrowser) {
        const baseUrl = window.location.pathname;
        window.history.replaceState(null, '', baseUrl);
      }
    }

    pushDataLayerEvent('filters_clear', {
      action_type: 'all filters - clear',
      click_text: 'Clear Filters',
      module_name: 'search filters',
      inpage_location: 'middle',
    });
  };

  const updateRoomOptions = (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const { checked, value } = event.target;
    const key = type === 'bedroomCount' ? 'bedroomSelected' : 'bathroomSelected';
    const filterType = type === 'bedroomCount' ? 'bedrooms' : 'bathrooms';

    setTempFilters((prev) => ({
      ...prev,
      [key]: checked ? [...prev[key], value] : prev[key].filter((v) => v !== value),
    }));

    pushDataLayerEvent('filters_selection', {
      action_type: `${filterType} - ${checked ? 'selected' : 'deselected'}`,
      action_value: `${value} ${filterType}`,
      click_text: `${value} ${filterType}`,
    });
  };

  const updateMoveIn = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;

    setTempFilters((prev) => ({
      ...prev,
      moveInSelected: checked
        ? [...prev.moveInSelected, value]
        : prev.moveInSelected.filter((v) => v !== value),
    }));

    pushDataLayerEvent('filters_selection', {
      action_type: `move in date - ${checked ? 'selected' : 'deselected'}`,
      action_value: `${value} Days`,
      click_text: `${value} Days`,
    });
  };

  const extractFilterValues = () => {
    const {
      minPrice,
      maxPrice,
      minLease,
      maxLease,
      bedroomSelected,
      bathroomSelected,
      moveInSelected,
    } = tempFilters;

    return {
      min: parseNumber(minPrice),
      max: parseNumber(maxPrice),
      minLeaseVal: parseNumber(minLease),
      maxLeaseVal: parseNumber(maxLease),
      bedroomSelected,
      bathroomSelected,
      moveInSelected,
      minPrice,
      maxPrice,
      minLease,
      maxLease,
    };
  };

  const applyFiltersAndUpdateState = (filterValues: ReturnType<typeof extractFilterValues>) => {
    const filtered = applyFilters({
      groupedFloorPlans,
      property,
      min: filterValues.min,
      max: filterValues.max,
      minLease: filterValues.minLeaseVal,
      maxLease: filterValues.maxLeaseVal,
      bedroomSelected: filterValues.bedroomSelected,
      bathroomSelected: filterValues.bathroomSelected,
      moveInSelected: filterValues.moveInSelected,
    });

    setMinPriceSelected(filterValues.minPrice);
    setMaxPriceSelected(filterValues.maxPrice);
    setMinLeaseSelected(filterValues.minLease);
    setMaxLeaseSelected(filterValues.maxLease);
    setBedroomSelected(filterValues.bedroomSelected);
    setBathroomSelected(filterValues.bathroomSelected);
    setMoveInSelected(filterValues.moveInSelected);
    setFilteredFloorPlans(filtered ?? []);
  };

  const updateUrlAndAnalytics = (filterValues: ReturnType<typeof extractFilterValues>) => {
    if (!isBrowser) return;

    const filterUrl = encodeFiltersToUrl({
      min: filterValues.minPrice,
      max: filterValues.maxPrice,
      minLease: filterValues.minLease,
      maxLease: filterValues.maxLease,
      bedroomSelected: filterValues.bedroomSelected,
      bathroomSelected: filterValues.bathroomSelected,
      moveInSelected: filterValues.moveInSelected,
    });

    const baseUrl = window.location.pathname;
    const fullUrl = filterUrl ? `${baseUrl}?${filterUrl}` : baseUrl;
    window.history.replaceState(null, '', fullUrl);

    trackAnalytics(filterValues);
  };

  const getLabelsFromSelected = (
    selected: string[],
    options: { value: string; label: string }[]
  ): string[] => {
    return selected
      .map((val) => options.find((opt) => opt.value === val)?.label)
      .filter((label): label is string => Boolean(label));
  };

  const trackAnalytics = (filterValues: ReturnType<typeof extractFilterValues>) => {
    const filtersApplied: string[] = [];
    if (filterValues.minPrice ?? filterValues.maxPrice) filtersApplied.push('budget');
    if (filterValues.bedroomSelected.length > 0 || filterValues.bathroomSelected.length > 0) {
      filtersApplied.push('property');
    }
    if (filterValues.moveInSelected.length > 0) filtersApplied.push('move in date');
    if (filterValues.minLease ?? filterValues.maxLease) filtersApplied.push('timeline');
    if (filtersApplied.length === 0) filtersApplied.push('all');

    const price_range_filter = `$${filterValues.minPrice ?? '0'} - $${
      filterValues.maxPrice ?? '0'
    }`;

    const bedroomLabels = getLabelsFromSelected(filterValues.bedroomSelected, bedroomFilterOptions);
    const bathroomLabels = getLabelsFromSelected(
      filterValues.bathroomSelected,
      bathroomFilterOptions
    );

    const sanitizeLabel = (label: string) =>
      label.replace(/\bBedroomss\b/, 'Bedrooms').replace(/\bBathroomss\b/, 'Bathrooms');

    const property_type_filter = [
      bedroomLabels.map(sanitizeLabel).join(', '),
      bathroomLabels.map(sanitizeLabel).join(', '),
    ]
      .filter(Boolean)
      .join(' | ');

    const moveInMap: Record<string, string> = {
      '7': '7 Days',
      '30': '30 Days',
      '60': '60 Days',
      '90': '90 Days',
      '180': '180 Days',
    };

    const lease = (val?: string | number) => {
      const num = Number(val);
      return num ? `${num} months` : '';
    };

    const leaseLength = [lease(filterValues.minLease), lease(filterValues.maxLease)]
      .filter(Boolean)
      .join(' - ');

    const moveInLabels = filterValues.moveInSelected
      .map((val) => moveInMap[val])
      .filter(Boolean)
      .join(', ');

    const timeline_filter = [moveInLabels, leaseLength].filter(Boolean).join(' | ');

    pushDataLayerEvent('filters_results', {
      action_type: 'all filters - show results',
      click_text: `Show ${filteredCount} results`,
      props: {
        price_range_filter,
        property_type_filter,
        property_features_filter: '',
        timeline_filter,
      },
    });
  };

  const showResults = () => {
    const filterValues = extractFilterValues();

    applyFiltersAndUpdateState(filterValues);

    updateUrlAndAnalytics(filterValues);

    setIsFilterVisible(false);
    if (isBrowser) document.body.style.overflow = 'auto';
  };

  const minSelectWithOptions = {
    options: formatPrices(
      allPrices,
      undefined,
      tempFilters.maxPrice ? parseInt(tempFilters.maxPrice, 10) : undefined
    ),
    defaultOption: {
      label: tempFilters.minPrice ? formatCurrency(parseInt(tempFilters.minPrice), '$') : 'Select',
      value: tempFilters.minPrice,
    },
    label: {
      header: 'Minimum Price',
    },
    selectProps: {
      id: 'min-price',
      name: 'min-price',
      onChange: updateMinPriceSelected,
    },
  };

  const maxSelectWithOptions = {
    options: formatPrices(
      allPrices,
      tempFilters.minPrice ? parseInt(tempFilters.minPrice, 10) : undefined
    ),
    defaultOption: {
      label: tempFilters.maxPrice ? formatCurrency(parseInt(tempFilters.maxPrice), '$') : 'Select',
      value: tempFilters.maxPrice,
    },
    label: {
      header: 'Maximum Price',
    },
    selectProps: {
      id: 'max-price',
      name: 'max-price',
      onChange: updateMaxPriceSelected,
    },
  };

  const minLeaseOptions = {
    options: formatLease(
      allLeaseTerms,
      undefined,
      tempFilters.maxLease ? parseInt(tempFilters.maxLease, 10) : undefined
    ),
    defaultOption: {
      label: tempFilters.minLease ? `${tempFilters.minLease}` : 'Select',
      value: tempFilters.minLease,
    },
    label: {
      header: 'Minimum Lease Term (months)',
    },
    selectProps: {
      id: 'min-lease',
      name: 'min-lease',
      onChange: updateMinLease,
    },
  };

  const maxLeaseOptions = {
    options: formatLease(
      allLeaseTerms,
      tempFilters.minLease ? parseInt(tempFilters.minLease, 10) : undefined
    ),
    defaultOption: {
      label: tempFilters.maxLease ? `${tempFilters.maxLease}` : 'Select',
      value: tempFilters.maxLease,
    },
    label: {
      header: 'Maximum Lease Term (months)',
    },
    selectProps: {
      id: 'max-lease',
      name: 'max-lease',
      onChange: updateMaxLease,
    },
  };

  const bedroomFilterOptions = getDynamicRoomOptions(
    groupedFloorPlans,
    'bedroomCount',
    'Bedroom',
    updateRoomOptions,
    true
  );
  const bathroomFilterOptions = getDynamicRoomOptions(
    groupedFloorPlans,
    'bathroomCount',
    'Bathroom',
    updateRoomOptions,
    false
  );

  const handleToggle = useCallback(
    ({ title, isHidden }: { title: string; isHidden: boolean }) => {
      if (isBrowser) {
        const event = isHidden ? 'filters_hide' : 'filters_show';
        const action = `${title.toLowerCase()} - ${isHidden ? 'hide' : 'show'}`;

        pushDataLayerEvent(event, {
          action_type: action,
          click_text: isHidden ? 'Hide' : 'Show',
        });
      }
    },
    [analytics, property, property_location]
  );

  return (
    <div data-testid="floor-plan-filters">
      <MiniButton
        variant="secondary"
        label={`${filterLabel}${
          getAppliedFilterGroupCount() > 0 ? ` (${getAppliedFilterGroupCount()})` : ''
        }`}
        onClick={openFilterModal}
        icon={{ name: 'Filter', position: 'right' }}
      />

      {isFilterVisible && (
        <Modal>
          <Modal.Header onClose={closeFilterModal}>
            <div className="filters-modal--title-wrapper">
              <p className="filters-modal--title-wrapper--title">Filters</p>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="filters-modal--body-wrapper">
              <FilterGroup>
                <FilterGroup.Header title={'Price'} handleToggle={handleToggle} />
                <FilterGroup.Body>
                  <RangeFilterType
                    key={`RANGEBUDGET-${tempFilters.minPrice}-${tempFilters.maxPrice}`}
                    heading="Total monthly leasing prices for the area you've searched."
                    minSelect={minSelectWithOptions}
                    maxSelect={maxSelectWithOptions}
                  />
                </FilterGroup.Body>
              </FilterGroup>
              <FilterGroup>
                <FilterGroup.Header title={RoomType.bedroom} handleToggle={handleToggle} />
                <FilterGroup.Body>
                  <CheckboxFilterType
                    title=""
                    filters={bedroomFilterOptions.map((option) => ({
                      ...option,
                      inputProps: {
                        ...option.inputProps,
                        checked: tempFilters.bedroomSelected.includes(option.value),
                      },
                    }))}
                  />
                </FilterGroup.Body>
              </FilterGroup>
              <FilterGroup>
                <FilterGroup.Header title={RoomType.bathroom} handleToggle={handleToggle} />
                <FilterGroup.Body>
                  <CheckboxFilterType
                    title=""
                    filters={bathroomFilterOptions.map((option) => ({
                      ...option,
                      inputProps: {
                        ...option.inputProps,
                        checked: tempFilters.bathroomSelected.includes(option.value),
                      },
                    }))}
                  />
                </FilterGroup.Body>
              </FilterGroup>
              <FilterGroup>
                <FilterGroup.Header title={'Move In Date'} handleToggle={handleToggle} />
                <FilterGroup.Body>
                  <CheckboxFilterType
                    title=""
                    filters={[
                      { label: '7 Days', value: '7', id: 'moveIn7' },
                      { label: '30 Days', value: '30', id: 'moveIn30' },
                      { label: '60 days', value: '60', id: 'moveIn60' },
                      { label: '90 days', value: '90', id: 'moveIn90' },
                      { label: '180 days', value: '180', id: 'moveIn180' },
                    ].map((opt) => ({
                      ...opt,
                      inputProps: {
                        onChange: updateMoveIn,
                        checked: tempFilters.moveInSelected.includes(opt.value),
                      },
                    }))}
                  />
                </FilterGroup.Body>
              </FilterGroup>
              {allLeaseTerms && allLeaseTerms.length > 0 && (
                <FilterGroup>
                  <FilterGroup.Header title={'Timeline'} handleToggle={handleToggle} />
                  <FilterGroup.Body>
                    <RangeFilterType
                      key={`LEASERANGE-${tempFilters.minLease}-${tempFilters.maxLease}`}
                      heading=""
                      minSelect={minLeaseOptions}
                      maxSelect={maxLeaseOptions}
                    />
                  </FilterGroup.Body>
                </FilterGroup>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="filters-modal--footer-wrapper">
              <div className="results-control--buttons-wrapper">
                <MiniButton
                  onClick={() => {
                    clearFilters();
                  }}
                  label="Clear Filters"
                  variant="text"
                />
                <CtaButton
                  onClick={showResults}
                  label={filteredCount > 0 ? `Show ${filteredCount} results` : 'No results'}
                  variant="primary"
                />
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default FloorPlanFilters;
